import React, { useEffect, useRef, useState } from 'react'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'
import WaveSurfer from 'wavesurfer.js'

interface ReproducedMixingPlayerProps {
  videoUrl: string
  prevAudioUrl: string
  nextAudioUrl: string
  transitionInfo: {
    prev: {
      artist: string
      title: string
      timestamp: number
    }
    next: {
      artist: string
      title: string
      timestamp: number
    }
  }
}

export const ReproducedMixingPlayer: React.FC<ReproducedMixingPlayerProps> = ({
  videoUrl,
  prevAudioUrl,
  nextAudioUrl,
  transitionInfo,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const mixWaveformRef = useRef<HTMLDivElement>(null)
  const prevWaveformRef = useRef<HTMLDivElement>(null)
  const nextWaveformRef = useRef<HTMLDivElement>(null)
  
  const mixWaveSurferRef = useRef<WaveSurfer | null>(null)
  const prevWaveSurferRef = useRef<WaveSurfer | null>(null)
  const nextWaveSurferRef = useRef<WaveSurfer | null>(null)
  
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [mixMuted, setMixMuted] = useState(false)
  const [prevMuted, setPrevMuted] = useState(false)
  const [nextMuted, setNextMuted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Format time helper
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Initialize wavesurfers
  useEffect(() => {
    if (!mixWaveformRef.current || !prevWaveformRef.current || !nextWaveformRef.current) return

    // Create wavesurfer for mix (from video audio)
    const mixWs = WaveSurfer.create({
      container: mixWaveformRef.current,
      waveColor: '#60a5fa',
      progressColor: '#2563eb',
      height: 60,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      backend: 'WebAudio',
    })

    // Create wavesurfer for prev track
    const prevWs = WaveSurfer.create({
      container: prevWaveformRef.current,
      waveColor: '#fb923c',
      progressColor: '#ea580c',
      height: 60,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      backend: 'WebAudio',
    })

    // Create wavesurfer for next track
    const nextWs = WaveSurfer.create({
      container: nextWaveformRef.current,
      waveColor: '#a78bfa',
      progressColor: '#7c3aed',
      height: 60,
      normalize: true,
      barWidth: 2,
      barGap: 1,
      backend: 'WebAudio',
    })

    mixWaveSurferRef.current = mixWs
    prevWaveSurferRef.current = prevWs
    nextWaveSurferRef.current = nextWs

    // Load audio files
    let loadedCount = 0
    const checkAllLoaded = () => {
      loadedCount++
      if (loadedCount === 3) {
        setIsLoading(false)
      }
    }

    mixWs.load(videoUrl)
    mixWs.on('ready', () => {
      console.log('Mix wavesurfer ready')
      checkAllLoaded()
      setDuration(mixWs.getDuration())
    })

    prevWs.load(prevAudioUrl)
    prevWs.on('ready', () => {
      console.log('Prev wavesurfer ready')
      checkAllLoaded()
    })

    nextWs.load(nextAudioUrl)
    nextWs.on('ready', () => {
      console.log('Next wavesurfer ready')
      checkAllLoaded()
    })

    // Sync on timeupdate
    mixWs.on('audioprocess', (time) => {
      setCurrentTime(time)
      if (prevWaveSurferRef.current) {
        prevWaveSurferRef.current.seekTo(time / mixWs.getDuration())
      }
      if (nextWaveSurferRef.current) {
        nextWaveSurferRef.current.seekTo(time / mixWs.getDuration())
      }
      // Sync video
      if (videoRef.current && Math.abs(videoRef.current.currentTime - time) > 0.1) {
        videoRef.current.currentTime = time
      }
    })

    mixWs.on('play', () => {
      setIsPlaying(true)
      if (prevWaveSurferRef.current && !prevMuted) {
        prevWaveSurferRef.current.play()
      }
      if (nextWaveSurferRef.current && !nextMuted) {
        nextWaveSurferRef.current.play()
      }
      if (videoRef.current) {
        videoRef.current.play()
      }
    })

    mixWs.on('pause', () => {
      setIsPlaying(false)
      if (prevWaveSurferRef.current) {
        prevWaveSurferRef.current.pause()
      }
      if (nextWaveSurferRef.current) {
        nextWaveSurferRef.current.pause()
      }
      if (videoRef.current) {
        videoRef.current.pause()
      }
    })

    mixWs.on('seek', (progress) => {
      const time = progress * mixWs.getDuration()
      setCurrentTime(time)
      if (prevWaveSurferRef.current) {
        prevWaveSurferRef.current.seekTo(progress)
      }
      if (nextWaveSurferRef.current) {
        nextWaveSurferRef.current.seekTo(progress)
      }
      if (videoRef.current) {
        videoRef.current.currentTime = time
      }
    })

    return () => {
      // Stop all playback before destroying
      if (mixWs) {
        mixWs.pause()
        mixWs.destroy()
      }
      if (prevWs) {
        prevWs.pause()
        prevWs.destroy()
      }
      if (nextWs) {
        nextWs.pause()
        nextWs.destroy()
      }
      // Also pause the video
      if (videoRef.current) {
        videoRef.current.pause()
      }
    }
  }, [videoUrl, prevAudioUrl, nextAudioUrl])

  // Handle mute changes
  useEffect(() => {
    if (mixWaveSurferRef.current) {
      mixWaveSurferRef.current.setVolume(mixMuted ? 0 : 1)
    }
  }, [mixMuted])

  useEffect(() => {
    if (prevWaveSurferRef.current) {
      prevWaveSurferRef.current.setVolume(prevMuted ? 0 : 1)
    }
  }, [prevMuted])

  useEffect(() => {
    if (nextWaveSurferRef.current) {
      nextWaveSurferRef.current.setVolume(nextMuted ? 0 : 1)
    }
  }, [nextMuted])

  const togglePlayPause = () => {
    if (mixWaveSurferRef.current) {
      mixWaveSurferRef.current.playPause()
    }
  }

  return (
    <div className="space-y-4">
      {/* Player Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={togglePlayPause}
            disabled={isLoading}
            className="p-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-full transition-colors"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <div className="flex-1">
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
          {isLoading && (
            <div className="text-sm text-gray-500 dark:text-gray-400">加载中...</div>
          )}
        </div>

        {/* Track Info */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">前曲</div>
            <div className="font-semibold text-gray-900 dark:text-white">{transitionInfo.prev.title}</div>
            <div className="text-gray-600 dark:text-gray-300">{transitionInfo.prev.artist}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              @ {formatTime(transitionInfo.prev.timestamp)}
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">后曲</div>
            <div className="font-semibold text-gray-900 dark:text-white">{transitionInfo.next.title}</div>
            <div className="text-gray-600 dark:text-gray-300">{transitionInfo.next.artist}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              @ {formatTime(transitionInfo.next.timestamp)}
            </div>
          </div>
        </div>

        {/* Waveforms */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setMixMuted(!mixMuted)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {mixMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <div className="flex-1">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Mix (原视频音频)</div>
              <div ref={mixWaveformRef} />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPrevMuted(!prevMuted)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {prevMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <div className="flex-1">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">前曲 (算法重建音频)</div>
              <div ref={prevWaveformRef} />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setNextMuted(!nextMuted)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {nextMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <div className="flex-1">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">后曲 (算法重建音频)</div>
              <div ref={nextWaveformRef} />
            </div>
          </div>
        </div>
      </div>

      {/* Video Player */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="aspect-video bg-black">
          <video
            ref={videoRef}
            className="w-full h-full"
            src={videoUrl}
            muted
          />
        </div>
      </div>
    </div>
  )
}

